import { useCallback, useEffect, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonContent,
  IonButton,
  IonIcon,
  IonTitle,
  IonActionSheet,
} from '@ionic/react';
import { shareOutline, documentTextOutline } from 'ionicons/icons';
import {
  QuickComposeRoot,
  QuickComposeGuestStrip,
  QuickComposeMessageArea,
  QuickComposeFooter,
  QuickComposeHeaderTitle,
  QuickComposeRecipientPopover,
  useQuickComposeContext,
} from '../components/QuickComposeWidget';
import ChatDiagonalPattern, {
  chatContentPatternClass,
} from '../components/ChatDiagonalPattern';
import { usePageView } from '../hooks/useLoggerInit';
import { useI18n } from '../i18n/I18nProvider';
import { deleteDraft, listDrafts, type MessageDraft } from '../lib/localStore';

const CHAT_BG = `
  radial-gradient(900px 520px at 12% -8%, rgba(var(--ion-color-primary-rgb), 0.2), transparent 64%),
  radial-gradient(860px 500px at 88% 4%, rgba(var(--ion-color-secondary-rgb), 0.16), transparent 68%),
  radial-gradient(760px 460px at 50% 100%, rgba(var(--ion-color-tertiary-rgb), 0.11), transparent 72%),
  linear-gradient(160deg, rgba(4, 10, 24, 0.99) 0%, rgba(8, 19, 41, 0.97) 48%, rgba(14, 30, 62, 0.95) 100%),
  var(--vault-surface)
`;

function QuickComposePageChrome({
  drafts,
  activeDraftId,
  onOpenDraft,
  onDeleteDraft,
}: {
  drafts: MessageDraft[];
  activeDraftId?: string;
  onOpenDraft: (id: string) => void;
  onDeleteDraft: (id: string) => void;
}) {
  const { t } = useI18n();
  const { handleShare, mode } = useQuickComposeContext();
  const [recipientOpen, setRecipientOpen] = useState(false);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const patternClass = chatContentPatternClass(mode);

  return (
    <IonPage className="chat-page quick-compose-page">
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" text={t('common.actions.back')} />
          </IonButtons>
          <IonTitle>
            <QuickComposeHeaderTitle onPress={() => setRecipientOpen(true)} />
          </IonTitle>
          <IonButtons slot="end">
            {drafts.length > 0 && (
              <IonButton
                aria-label={t('quickDashboard.draftsTitle')}
                onClick={() => setDraftsOpen(true)}
              >
                <IonIcon slot="icon-only" icon={documentTextOutline} />
              </IonButton>
            )}
            <IonButton aria-label={t('quickCompose.share')} onClick={handleShare}>
              <IonIcon slot="icon-only" icon={shareOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <QuickComposeRecipientPopover
        isOpen={recipientOpen}
        onDismiss={() => setRecipientOpen(false)}
        triggerId="quick-recipient-trigger"
      />

      <IonActionSheet
        isOpen={draftsOpen}
        onDidDismiss={() => setDraftsOpen(false)}
        header={t('quickDashboard.draftsTitle')}
        buttons={[
          ...drafts.map((draft) => ({
            text: draft.body.slice(0, 48) || t('quickDashboard.emptyDraft'),
            handler: () => onOpenDraft(draft.id),
          })),
          ...(activeDraftId
            ? [
                {
                  text: t('quickCompose.clearDraft'),
                  role: 'destructive' as const,
                  handler: () => onDeleteDraft(activeDraftId),
                },
              ]
            : []),
          { text: t('common.actions.cancel'), role: 'cancel' as const },
        ]}
      />

      <IonContent
        fullscreen
        className={`chat-content vault-screen quick-compose-content ${patternClass}`}
        style={{ '--background': CHAT_BG } as React.CSSProperties}
        fixedSlotPlacement="before"
      >
        <ChatDiagonalPattern mode={mode} />
        <QuickComposeGuestStrip />
        <QuickComposeMessageArea />
      </IonContent>

      <QuickComposeFooter asFooter />
    </IonPage>
  );
}

const QuickDashboardPage: React.FC = () => {
  const [drafts, setDrafts] = useState<MessageDraft[]>([]);
  const [activeDraftId, setActiveDraftId] = useState<string | undefined>();

  usePageView('quick');

  const refreshDrafts = useCallback(() => {
    setDrafts(listDrafts().filter((d) => d.status !== 'sent' && d.status !== 'migrated'));
  }, []);

  useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  const handleOpenDraft = (id: string) => {
    setActiveDraftId(id);
  };

  const handleDeleteDraft = (id: string) => {
    deleteDraft(id);
    if (activeDraftId === id) setActiveDraftId(undefined);
    refreshDrafts();
  };

  return (
    <QuickComposeRoot
      variant="anonymous"
      draftId={activeDraftId}
      onSaved={refreshDrafts}
    >
      <QuickComposePageChrome
        drafts={drafts}
        activeDraftId={activeDraftId}
        onOpenDraft={handleOpenDraft}
        onDeleteDraft={handleDeleteDraft}
      />
    </QuickComposeRoot>
  );
};

export default QuickDashboardPage;
